'use client';

import { useState } from 'react';

import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { resolveMatrixNaLabel } from '@/shared-project';
import { MATRIX_ANSWER_NOT_APPLICABLE } from '@/shared-question-model';
import { ReportProblem } from '@/shared-ui';

import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import { renderValue } from './project-response-rating-scale-select.helpers';
import { useRatingScaleSelect } from './project-response-rating-scale-select.hooks';
import { ProjectResponseComponentProps } from './project-response-renderer.type';
import {
  type MatrixSelection,
  parseMatrixSelection,
  toMatrixAnswerContent,
} from './shared-project-response-message-renderers-matrix-renderer.helpers';

const EMPTY_OPTIONS: NonNullable<ProjectResponseComponentProps['options']> = [];
const EMPTY_ROWS: NonNullable<ProjectResponseComponentProps['subQuestions']> =
  [];
const COMPACT_COLUMN_THRESHOLD = 5;

const MATRIX_BUTTON_CLASS_NAME =
  'flex h-9 items-center justify-center rounded-full border border-(--brand-separation-border) bg-white text-center [font-family:var(--brand-font)] text-sm text-gray-900 shadow-(--brand-separation-shadow) active:ring-2 active:ring-sky-300 disabled:cursor-default data-[selected=true]:border-none data-[selected=true]:bg-(--brand-primary) data-[selected=true]:text-(--brand-primary-contrast) data-[selected=true]:shadow-xl';
const MATRIX_OPTION_BUTTON_CLASS_NAME = cn(
  MATRIX_BUTTON_CLASS_NAME,
  'min-w-0 flex-1 px-1 data-[compact=true]:px-0.5'
);
const MATRIX_NA_BUTTON_CLASS_NAME = cn(
  MATRIX_BUTTON_CLASS_NAME,
  'shrink-0 px-3 whitespace-nowrap'
);

export default function SharedProjectResponseMessageRenderersMatrixRenderer({
  id,
  options = EMPTY_OPTIONS,
  subQuestions = EMPTY_ROWS,
  notApplicableLabel,
  text,
  value,
  onChange,
  readonly,
  optionRenderType,
  audio,
  isAiVoiceEnabled,
  isFollowUp = false,
  onSubmit,
  questionId,
  numeralLocale,
}: ProjectResponseComponentProps) {
  const t = useTranslations('AIForms');

  const [draft, setDraft] = useState<MatrixSelection>({});

  const { sortedOptions, labels } = useRatingScaleSelect(options);

  const rows = subQuestions ?? EMPTY_ROWS;
  const selection = readonly ? parseMatrixSelection(value) : draft;
  const naLabel = resolveMatrixNaLabel(
    notApplicableLabel,
    t('matrix.notApplicableDefault')
  );
  const isCompact = sortedOptions.length > COMPACT_COLUMN_THRESHOLD;

  const handleSelect = (rowId: string, optionValue: string) => {
    if (readonly) return;
    const next = { ...draft, [rowId]: optionValue };
    setDraft(next);
    onChange?.(toMatrixAnswerContent(rows, next));
  };

  return (
    <div
      data-test='matrix-renderer'
      className='isolate flex w-full flex-col gap-1'
    >
      <div className='flex flex-col gap-4 rounded-lg border border-(--brand-separation-border) bg-(--brand-assistant-message-bg) p-3 shadow-(--brand-separation-shadow) backdrop-blur-sm'>
        {isAiVoiceEnabled && (
          <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
        )}

        <ProjectResponseQuestionText
          text={text}
          className='text-(--brand-text-on-assistant-message)'
        />

        {labels[0] && labels[1] && (
          <div
            data-test='matrix-renderer-scale-labels'
            className='flex items-start justify-between gap-2 [font-family:var(--brand-font)] text-xs text-(--brand-text-on-assistant-message)'
          >
            <span>{`${sortedOptions[0]?.value} = ${labels[0]}`}</span>
            <span className='text-right'>{`${sortedOptions.at(-1)?.value} = ${labels[1]}`}</span>
          </div>
        )}

        {rows.map((row) => (
          <fieldset
            key={row.id}
            data-test='matrix-renderer-row'
            className='min-w-0'
          >
            <legend className='mb-2 [font-family:var(--brand-font)] text-sm font-medium text-(--brand-text-on-assistant-message)'>
              {row.text}
            </legend>
            <div className='flex w-full flex-wrap items-center gap-1'>
              <div
                data-compact={isCompact}
                className='flex min-w-0 flex-1 gap-1 data-[compact=true]:basis-full data-[compact=true]:gap-0.5'
              >
                {sortedOptions.map((option) => (
                  <button
                    key={option.value}
                    type='button'
                    data-selected={selection[row.id] === option.value}
                    data-compact={isCompact}
                    aria-pressed={selection[row.id] === option.value}
                    aria-label={`${t('matrix.rateAriaPrefix')} ${row.text}: ${option.label}`}
                    disabled={readonly}
                    onClick={() => handleSelect(row.id, option.value)}
                    className={MATRIX_OPTION_BUTTON_CLASS_NAME}
                    data-test='matrix-renderer-option-button'
                  >
                    {renderValue(
                      option,
                      optionRenderType,
                      sortedOptions,
                      numeralLocale
                    )}
                  </button>
                ))}
              </div>
              {naLabel && (
                <button
                  type='button'
                  data-selected={
                    selection[row.id] === MATRIX_ANSWER_NOT_APPLICABLE
                  }
                  aria-pressed={
                    selection[row.id] === MATRIX_ANSWER_NOT_APPLICABLE
                  }
                  aria-label={t('matrix.markNaAria', { row: row.text })}
                  disabled={readonly}
                  onClick={() =>
                    handleSelect(row.id, MATRIX_ANSWER_NOT_APPLICABLE)
                  }
                  className={MATRIX_NA_BUTTON_CLASS_NAME}
                  data-test='matrix-renderer-na-button'
                >
                  {naLabel}
                </button>
              )}
            </div>
          </fieldset>
        ))}
      </div>
      {onSubmit && id && (
        <ReportProblem
          context={{ messageId: id, questionId: questionId ?? undefined }}
          onSubmit={onSubmit}
          variant={isFollowUp ? 'follow-up' : 'main'}
          className='self-start'
        />
      )}
    </div>
  );
}
